import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveUserMeasurements, saveUserMeasurementHistory, getUserMeasurementHistory } from '../utils/userDataManager';
import './WeeklyCheckIn.css';

function WeeklyCheckIn() {
  const navigate = useNavigate();
  const [measurements, setMeasurements] = useState({
    weight: '',
    bodyFat: '',
    bloodPressureSystolic: '',
    bloodPressureDiastolic: '',
    restingHeartRate: '',
    bicepLeft: '',
    bicepRight: '',
    chest: '',
    waist: '',
    thighLeft: '',
    thighRight: '',
    fatFreeMass: '',
    notes: ''
  });

  const handleChange = (field, value) => {
    setMeasurements(prev => ({ ...prev, [field]: value }));
  };

  const calculateFatFreeMass = () => {
    const weight = parseFloat(measurements.weight);
    const bodyFat = parseFloat(measurements.bodyFat);

    if (!isNaN(weight) && weight > 0 &&
        !isNaN(bodyFat) && bodyFat > 0 && bodyFat < 100) {
      const ffm = weight * (1 - bodyFat / 100);
      setMeasurements(prev => ({ ...prev, fatFreeMass: ffm.toFixed(1) }));
    }
  };

  const handleSave = () => {
    // Add timestamp
    const checkInData = {
      ...measurements,
      date: new Date().toISOString(),
      week: getWeekNumber()
    };

    // Save as current measurements
    saveUserMeasurements(measurements);

    // Add to history
    const history = getUserMeasurementHistory();
    history.push(checkInData);
    saveUserMeasurementHistory(history);

    // Mark check-in as complete for this week
    localStorage.setItem(`user_${localStorage.getItem('currentUser')}_lastCheckIn`, new Date().toISOString());

    // Navigate to weekly planner with data
    navigate('/weekly-planner', { state: { checkInData } });
  };

  const handleSkip = () => {
    // Go directly to planner without new measurements
    navigate('/weekly-planner');
  };

  const getWeekNumber = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = now - start;
    const oneWeek = 1000 * 60 * 60 * 24 * 7;
    return Math.floor(diff / oneWeek) + 1;
  };

  const isFormValid = measurements.weight && measurements.bodyFat;

  return (
    <div className="check-in-screen">
      <div className="check-in-container">
        <div className="check-in-header">
          <h1 className="check-in-title">📊 Weekly Check-In</h1>
          <p className="check-in-subtitle">
            Track your progress to help Claude design better workouts
          </p>
          <p className="week-badge">Week {getWeekNumber()} of {new Date().getFullYear()}</p>
        </div>

        <div className="check-in-card">
          <div className="metric-section">
            <h3 className="section-title">⚖️ Body Composition</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Weight (lbs) *</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="175.0"
                  value={measurements.weight}
                  onChange={(e) => handleChange('weight', e.target.value)}
                  onBlur={calculateFatFreeMass}
                />
              </div>

              <div className="form-group">
                <label>Body Fat % *</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="20.0"
                  value={measurements.bodyFat}
                  onChange={(e) => handleChange('bodyFat', e.target.value)}
                  onBlur={calculateFatFreeMass}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Fat-Free Mass (lbs)</label>
              <input
                type="number"
                step="0.1"
                placeholder="Auto-calculated"
                value={measurements.fatFreeMass}
                onChange={(e) => handleChange('fatFreeMass', e.target.value)}
                readOnly
                className="calculated-field"
              />
              <p className="field-hint">Auto-calculated: Weight × (1 - Body Fat %)</p>
            </div>
          </div>

          <div className="metric-section">
            <h3 className="section-title">❤️ Cardiovascular Health</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Blood Pressure</label>
                <div className="bp-inputs">
                  <input
                    type="number"
                    placeholder="120"
                    value={measurements.bloodPressureSystolic}
                    onChange={(e) => handleChange('bloodPressureSystolic', e.target.value)}
                  />
                  <span className="bp-separator">/</span>
                  <input
                    type="number"
                    placeholder="80"
                    value={measurements.bloodPressureDiastolic}
                    onChange={(e) => handleChange('bloodPressureDiastolic', e.target.value)}
                  />
                </div>
                <p className="field-hint">Systolic / Diastolic (mmHg)</p>
              </div>

              <div className="form-group">
                <label>Resting Heart Rate</label>
                <input
                  type="number"
                  placeholder="65"
                  value={measurements.restingHeartRate}
                  onChange={(e) => handleChange('restingHeartRate', e.target.value)}
                />
                <p className="field-hint">Beats per minute (BPM)</p>
              </div>
            </div>
          </div>

          <div className="metric-section">
            <h3 className="section-title">📏 Body Measurements</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Bicep - Left (inches)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="14.5"
                  value={measurements.bicepLeft}
                  onChange={(e) => handleChange('bicepLeft', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Bicep - Right (inches)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="14.5"
                  value={measurements.bicepRight}
                  onChange={(e) => handleChange('bicepRight', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Chest (inches)</label>
              <input
                type="number"
                step="0.1"
                placeholder="40.0"
                value={measurements.chest}
                onChange={(e) => handleChange('chest', e.target.value)}
              />
              <p className="field-hint">Measured at nipple level, relaxed</p>
            </div>

            <div className="form-group">
              <label>Waist (inches)</label>
              <input
                type="number"
                step="0.1"
                placeholder="34.0"
                value={measurements.waist}
                onChange={(e) => handleChange('waist', e.target.value)}
              />
              <p className="field-hint">Measured at belly button level</p>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Thigh - Left (inches)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="22.0"
                  value={measurements.thighLeft}
                  onChange={(e) => handleChange('thighLeft', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Thigh - Right (inches)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="22.0"
                  value={measurements.thighRight}
                  onChange={(e) => handleChange('thighRight', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="metric-section">
            <h3 className="section-title">📝 Notes</h3>
            <textarea
              className="notes-textarea"
              placeholder="How did you feel this week? Any injuries, sleep issues, or other notes..."
              value={measurements.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
            />
          </div>

          <div className="required-note">
            * Weight and Body Fat % are required
          </div>

          <div className="button-row">
            <button className="btn-skip" onClick={handleSkip}>
              Skip & Generate Plan
            </button>
            <button
              className="btn-save-checkin"
              onClick={handleSave}
              disabled={!isFormValid}
            >
              ✅ Save & Generate Plan
            </button>
          </div>
        </div>

        <div className="info-box">
          <h4 className="info-title">💡 Tips for Accurate Measurements</h4>
          <ul className="info-list">
            <li>📅 Measure at the same time each week (e.g., Sunday morning)</li>
            <li>⏰ Weigh yourself first thing in the morning, after bathroom, before eating</li>
            <li>📏 Use a flexible tape measure, keep it snug but not tight</li>
            <li>💪 Flex muscles for bicep/thigh measurements</li>
            <li>❤️ Rest for 5 minutes before taking heart rate/blood pressure</li>
            <li>📊 Body fat can be measured with a scale, calipers, or estimated</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default WeeklyCheckIn;
